import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ImageUpload } from '~/Components/ImageUpload';
import { PRIMARY_BLUE, IMAGE_GRADIENT, WHITE } from '~/Constants/Colors.constants';
import { PLACEHOLDERS } from '~/Constants/Images.constants';

import { UploadedImage } from '~/Models/interfaces/UploadedImage.model';
import { requestClearImageUpload, selectImageUpload } from '~/store/app';
import { Button } from '~/UIKit';
import { useMediaQuery, useTheme } from '@mui/material';
import { ExtendedEventGroup } from '../../../ExtendedEventGroup.model';
import { connect, FormikContextType } from 'formik';
import { makeStyles } from '~/UIKit/Style';

interface EventImageUploadProps {
  disabled?: boolean;
}

const useStyles = (uploadedImage: UploadedImage, disabled: boolean) =>
  makeStyles((theme) => ({
    root: {
      margin: '0 15px 15px',
      height: 165,
      backgroundPosition: 'center',
      position: 'relative',
      background: uploadedImage.url
        ? `url(${uploadedImage.url})`
        : `${IMAGE_GRADIENT},url(${PLACEHOLDERS.Wellness})`,
      backgroundSize: 'cover',
      [theme.breakpoints.up('md')]: {
        margin: 0,
        paddingTop: '38.5%',
      },
      opacity: disabled ? 0.4 : 1,
    },
    uploadButtonContainer: {
      position: 'absolute',
      right: '15px',
      bottom: '15px',
      '& .MuiButtonBase-root': {
        border: `1px solid ${PRIMARY_BLUE}`,
        color: WHITE,
        backgroundColor: PRIMARY_BLUE,
      },
      '& .dot': {
        backgroundColor: WHITE,
      },
      [theme.breakpoints.up('md')]: {
        top: '15px',
        bottom: '0',
      },
    },
    uploadButtonContainerDefault: {
      position: 'absolute',
      margin: 'auto',
      width: '130px',
      height: '35px',
      right: '15px',
      bottom: '15px',
      [theme.breakpoints.up('md')]: {
        top: '15px',
        right: '15px',
        bottom: 'unset',
      },
      '& .MuiButtonBase-root': {
        border: `1px solid ${PRIMARY_BLUE}`,
        color: WHITE,
        backgroundColor: PRIMARY_BLUE,
        width: '100%',
      },
    },
    deleteButton: {
      marginLeft: '20px',
      [theme.breakpoints.down('lg')]: {
        display: 'none',
      },
    },
  }));

export const EventImageUpload = connect<EventImageUploadProps, ExtendedEventGroup>(
  ({
    disabled,
    formik,
  }: EventImageUploadProps & { formik: FormikContextType<ExtendedEventGroup> }) => {
    const dispatch = useDispatch();
    const uploadedImage = useSelector(selectImageUpload);
    const clearImage = bindActionCreators(requestClearImageUpload, dispatch);
    const { classes } = useStyles(uploadedImage, !!disabled)();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
      if (uploadedImage.url) {
        formik.setFieldValue('imageUrl', uploadedImage.url);
      }
    }, [uploadedImage]);

    useEffect(() => {
      //clear image in the first load
      clearImage();
    }, []);

    return (
      <div className={classes.root}>
        <div
          className={
            uploadedImage.url ? classes.uploadButtonContainer : classes.uploadButtonContainerDefault
          }
        >
          <ImageUpload
            disabled={disabled}
            canvasStyle={{
              width: isMobile ? 300 : 600,
              height: isMobile ? 115 : 230,
              color: [0, 0, 0, 0.4],
              borderRadius: 0,
              border: 10,
            }}
          />
          {uploadedImage.url && (
            <Button
              color="primary"
              component="span"
              variant="outlined"
              className={classes.deleteButton}
              onClick={clearImage}
              disabled={disabled}
            >
              {t('app.delete')}
            </Button>
          )}
        </div>
      </div>
    );
  },
);
